/**
 * AdminMessagesBell — EVENTSFLO-CREW-PWA-MESSAGE-CENTRE-V1
 *
 * A bell icon button that:
 *  - polls the admin Message Centre unread count
 *  - opens a Sheet drawer listing recent messages
 *  - lets the admin click a message to navigate to the relevant link
 *  - lets the admin mark all as read
 *
 * Designed to drop into any admin page header (e.g. the Crew Planner toolbar).
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Inbox,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  getAdminMessages,
  getAdminMessagesUnreadCount,
  markAdminMessageRead,
  markAllAdminMessagesRead,
} from "../../lib/api";

const TYPE_ICON = {
  shift_accepted: CheckCircle2,
  shift_rejected: XCircle,
  shift_withdrawn_by_crew: AlertTriangle,
  profile_update_request: Inbox,
  shift_cancelled_by_admin: AlertTriangle,
  shift_request: Bell,
  admin_message: Bell,
};

const TYPE_TINT = {
  shift_accepted: "text-emerald-400",
  shift_rejected: "text-red-400",
  shift_withdrawn_by_crew: "text-orange-400",
  profile_update_request: "text-blue-400",
  shift_cancelled_by_admin: "text-zinc-400",
  shift_request: "text-amber-400",
  admin_message: "text-primary",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function AdminMessagesBell({ className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const res = await getAdminMessagesUnreadCount();
      setUnread(res.data?.unread_count || 0);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminMessages(false, 30);
      setMessages(res.data || []);
    } catch (err) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) loadMessages();
  };

  const handleClick = async (msg) => {
    if (!msg.read_at) {
      try {
        await markAdminMessageRead(msg.id);
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, read_at: new Date().toISOString() } : m)),
        );
        loadCount();
      } catch {
        /* non-fatal */
      }
    }
    if (msg.link) {
      setOpen(false);
      navigate(msg.link);
    }
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await markAllAdminMessagesRead();
      setMessages((prev) =>
        prev.map((m) => ({ ...m, read_at: m.read_at || new Date().toISOString() })),
      );
      setUnread(0);
      toast.success("All messages marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative ${className}`}
          data-testid="admin-messages-bell"
          aria-label={`Open messages (${unread} unread)`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="admin-messages-unread-count"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>Messages</span>
            {unread > 0 && (
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/40">
                {unread} unread
              </Badge>
            )}
          </SheetTitle>
          <SheetDescription>
            Crew action notifications. Tap a message to jump to context.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="admin-messages-empty">
              <Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No messages yet</p>
            </div>
          ) : (
            <ul className="space-y-2 py-2">
              {messages.map((msg) => {
                const Icon = TYPE_ICON[msg.type] || Bell;
                const tint = TYPE_TINT[msg.type] || "text-primary";
                const unreadRow = !msg.read_at;
                return (
                  <li
                    key={msg.id}
                    onClick={() => handleClick(msg)}
                    className={`cursor-pointer rounded-lg border p-3 transition hover:border-primary/50 ${
                      unreadRow ? "border-primary/40 bg-primary/5" : "border-border bg-card"
                    }`}
                    data-testid={`admin-message-${msg.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${tint}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold truncate">{msg.title}</p>
                          {unreadRow && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{msg.body}</p>
                        <p className="text-[11px] text-muted-foreground/70 mt-1">
                          {timeAgo(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {messages.length > 0 && unread > 0 && (
          <div className="pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleMarkAllRead}
              disabled={busy}
              data-testid="admin-messages-mark-all-read"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Mark all as read
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
